import { Box } from "@mui/material"
import { useLoaderData, useParams, useSearch } from "@tanstack/react-router"
import { type ReactElement, memo, useCallback, useEffect, useRef, useState } from "react"

import { useProductFileHandling } from "./hooks/useProductFileHandling"
import { useProductFormHandlers } from "./hooks/useProductFormHandlers"
import { ProductFormDetailsDialog } from "./ProductFormDetailsDialog"
import { ProductFormGrid } from "./ProductFormGrid"
import { ProductFormPicturesEdit } from "./ProductFormPicturesEdit"
import { ProductFormPicturesPreview } from "./ProductFormPicturesPreview"
import { useProductFormImagesStore } from "./stores/useProductFormImagesStore"
import type { ProductFormData } from "./types"
import { ProductFormDialog } from "./types"

import { ProductFormInfoItem } from "@/graphql/codegen/graphql"
import { useGetProduct } from "@/screens/Products/hooks/useGetProduct"
import type { ProductInformationLoaderResult } from "@/screens/Products/routeLoader"

export const ProductFormContainer = memo(
  ({ isPastProductVersion }: { isPastProductVersion: boolean }): ReactElement => {
    const { productForm, units, productVersionInfo } = useLoaderData({
      from: "/app/product/$productId/edit/_layout/information",
    }) as ProductInformationLoaderResult

    const forms = useRef(Object.entries(ProductFormInfoItem).map(([key, value]) => ({ id: value, name: key }))).current

    const [formState, setFormState] = useState<ProductFormData>({
      productFormInfo: productForm.productFormInfo || ProductFormInfoItem.Liquid,
      uomId: productForm.uomId || "",
      signedUrls: productForm.signedUrls || [],
    })
    const [openedDialog, setOpenedDialog] = useState<ProductFormDialog | null>(null)
    const { productId } = useParams({ from: "/app/product/$productId/edit/_layout/information" })
    const { version } = useSearch({ from: "/app/product/$productId/edit/_layout/information" })

    const { handleUpdateProduct } = useProductFormHandlers(productId, version)
    const { previewFiles, uploading, handleFileSelect, handleSaveFiles, handleCancel } = useProductFileHandling(
      productId,
      productVersionInfo?.versionId
    )
    const { clearPreviews, getAllImageUrls, setSignedUrls } = useProductFormImagesStore()
    const { productDetails } = useGetProduct(Number(productId), version)
    const imageUrls = getAllImageUrls()

    const handleClose = useCallback(() => {
      handleCancel()
      setOpenedDialog(null)
    }, [handleCancel])

    const handleFormSubmit = useCallback(
      async (formData: ProductFormData) => {
        try {
          const mergedSignedUrls = await handleSaveFiles()

          const updatedFormData = {
            ...formData,
            signedUrls: mergedSignedUrls,
          } satisfies ProductFormData

          const success = await handleUpdateProduct(updatedFormData)
          if (success) {
            setFormState((prev) => ({
              ...prev,
              ...updatedFormData,
            }))
            setSignedUrls(mergedSignedUrls)
            setOpenedDialog(null)
          }
        } catch (error) {
          console.error("Failed to submit form:", error)
        }
      },
      [handleUpdateProduct, handleSaveFiles, setSignedUrls]
    )

    const handleEditDetails = useCallback(() => {
      setOpenedDialog(ProductFormDialog.EditForm)
    }, [])

    const handleViewPictures = useCallback(() => {
      setOpenedDialog(ProductFormDialog.PreviewImages)
    }, [])

    const handleEditPictures = useCallback(() => {
      setOpenedDialog(ProductFormDialog.EditPictures)
    }, [])

    const handlePicturesSubmit = useCallback(async () => {
      await handleFormSubmit(formState)
    }, [handleFormSubmit, formState])

    // Initialize store with signed URLs
    useEffect(() => {
      setSignedUrls(formState.signedUrls || [])
    }, [formState.signedUrls, setSignedUrls])

    useEffect(() => {
      if (!productDetails) return

      const updatedForm = {
        productFormInfo: productDetails.productFormInfo,
        uomId: productDetails.uomId,
        signedUrls: productForm.signedUrls,
      } as ProductFormData

      setFormState(updatedForm)
    }, [productDetails, productForm.signedUrls])

    useEffect(() => {
      clearPreviews()
    }, [clearPreviews, productId])

    return (
      <Box className='flex flex-col gap-6'>
        <ProductFormGrid
          formInfo={formState.productFormInfo}
          uomId={formState.uomId}
          imageUrls={imageUrls}
          units={units}
          isPastProductVersion={isPastProductVersion}
          onEditDetails={handleEditDetails}
          onViewPictures={handleViewPictures}
        />

        <ProductFormDetailsDialog
          open={openedDialog === ProductFormDialog.EditForm}
          formOptions={forms}
          unitOfMeasurementOptions={units}
          onClose={handleClose}
          onSubmit={handleFormSubmit}
          initialData={formState}
          previewFiles={previewFiles}
          onFileSelect={handleFileSelect}
          uploading={uploading}
        />

        <ProductFormPicturesPreview
          open={openedDialog === ProductFormDialog.PreviewImages}
          onClose={() => setOpenedDialog(null)}
          onEdit={handleEditPictures}
          imageUrls={imageUrls}
        />

        <ProductFormPicturesEdit
          open={openedDialog === ProductFormDialog.EditPictures}
          onClose={handleClose}
          onSubmit={handlePicturesSubmit}
          signedUrls={formState.signedUrls}
          previewFiles={previewFiles}
          onFileSelect={handleFileSelect}
          uploading={uploading}
        />
      </Box>
    )
  }
)

ProductFormContainer.displayName = "ProductFormContainer"
